import { ColorPicker } from '@src/ui/elements/color/ColorPicker';
import { pickCellColor } from '@src/utils/colorPickerUtil';
import { findParentNode } from '@tiptap/core';
import { Editor } from '@tiptap/react';
import { Node as PMNode } from 'prosemirror-model';
import { CellSelection, selectedRect } from 'prosemirror-tables';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaEllipsisV, FaMinus, FaPlus } from 'react-icons/fa';

import { EditorFeatures } from '../types';

/**
 * Utility function to get combined bounding rect for multiple elements
 */
function getCombinedBoundingRect(elements: HTMLElement[]): DOMRect {
  const rects = elements.map((el) => el.getBoundingClientRect());
  const left = Math.min(...rects.map((r) => r.left));
  const top = Math.min(...rects.map((r) => r.top));
  const right = Math.max(...rects.map((r) => r.right));
  const bottom = Math.max(...rects.map((r) => r.bottom));
  return new DOMRect(left, top, right - left, bottom - top);
}

export interface EditorTableToolbarProps {
  editor: Editor | null;
  features: EditorFeatures;
}

/**
 * EditorTableToolbar
 * Floating toolbar that appears when table cells are selected
 * Provides row/column operations and cell styling options
 */
export const EditorTableToolbar: React.FC<EditorTableToolbarProps> = ({ editor, features }) => {
  const [selectionRect, setSelectionRect] = useState<DOMRect | null>(null);
  const [showCellMenu, setShowCellMenu] = useState(false);
  const [recentCellColor, setRecentCellColor] = useState('#ffffff');
  const toolbarRef = useRef<HTMLDivElement | null>(null);

  const handlePickCellColor = useCallback(
    (color: string) => {
      if (!editor) {
        return;
      }
      pickCellColor(editor, color, setRecentCellColor);
      setShowCellMenu(false);
    },
    [editor],
  );

  const handleAddRow = useCallback(() => {
    if (!editor) {
      return;
    }
    editor.view.dom.focus({ preventScroll: true });
    editor.chain().addRowAfter().run();
  }, [editor]);

  const handleDeleteRow = useCallback(() => {
    if (!editor) {
      return;
    }
    editor.view.dom.focus({ preventScroll: true });
    editor.chain().deleteRow().run();
  }, [editor]);

  const handleAddColumn = useCallback(() => {
    if (!editor) {
      return;
    }
    editor.view.dom.focus({ preventScroll: true });
    editor.chain().addColumnAfter().run();
  }, [editor]);

  const handleDeleteColumn = useCallback(() => {
    if (!editor) {
      return;
    }
    editor.view.dom.focus({ preventScroll: true });
    editor.chain().deleteColumn().run();
  }, [editor]);

  // Update toolbar position based on cell selection
  const updateToolbarPosition = useCallback(() => {
    if (!editor) {
      return;
    }

    const { selection } = editor.state;

    if (selection instanceof CellSelection) {
      // Multiple cells selected
      const cells: HTMLElement[] = [];
      selection.forEachCell((_cellNode, pos) => {
        const dom = editor.view.nodeDOM(pos) as HTMLElement;
        if (dom) {
          cells.push(dom);
        }
      });

      if (cells.length > 0) {
        const rect = getCombinedBoundingRect(cells);
        setSelectionRect(rect);
        return;
      }
    } else {
      // Single cell cursor position
      const cell = findParentNode(
        (node: PMNode) => node.type === editor.state.schema.nodes.tableCell,
      )(editor.state.selection);

      if (cell) {
        const cellDOM = editor.view.nodeDOM(cell.pos) as HTMLElement;
        if (cellDOM) {
          const rect = cellDOM.getBoundingClientRect();
          setSelectionRect(new DOMRect(rect.left, rect.top, rect.width, rect.height));
          return;
        }
      }
    }

    setSelectionRect(null);
    setShowCellMenu(false);
  }, [editor]);

  // Hide toolbar on scroll
  const hideToolbar = useCallback(() => {
    setSelectionRect(null);
    setShowCellMenu(false);
  }, []);

  // Click outside handler
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (toolbarRef.current && !toolbarRef.current.contains(event.target as Node)) {
        setShowCellMenu(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!editor) {
      return;
    }

    editor.on('selectionUpdate', updateToolbarPosition);

    // Find all scrollable containers
    const editorElement = editor.view.dom;
    const parent = editorElement.parentElement;
    const grandParent = parent?.parentElement;
    const scrollableContainers: HTMLElement[] = [];

    if (parent) {
      scrollableContainers.push(parent);
    }
    if (grandParent) {
      scrollableContainers.push(grandParent);
    }

    // Find scrollable ancestors
    let current = editorElement.parentElement;
    while (current && current !== document.body) {
      const computedStyle = window.getComputedStyle(current);
      if (
        computedStyle.overflow === 'auto' ||
        computedStyle.overflow === 'scroll' ||
        computedStyle.overflowY === 'auto' ||
        computedStyle.overflowY === 'scroll'
      ) {
        if (!scrollableContainers.includes(current)) {
          scrollableContainers.push(current);
        }
      }
      current = current.parentElement;
    }

    // Add scroll listeners
    scrollableContainers.forEach((container) => {
      container.addEventListener('scroll', hideToolbar, { passive: true });
    });
    window.addEventListener('scroll', hideToolbar, { passive: true });
    window.addEventListener('resize', updateToolbarPosition);

    return () => {
      editor.off('selectionUpdate', updateToolbarPosition);
      scrollableContainers.forEach((container) => {
        container.removeEventListener('scroll', hideToolbar);
      });
      window.removeEventListener('scroll', hideToolbar);
      window.removeEventListener('resize', updateToolbarPosition);
    };
  }, [editor, updateToolbarPosition, hideToolbar]);

  // Don't render if tables are not enabled or no selection
  if (!features.tables || !selectionRect || !editor) {
    return null;
  }

  // Determine if we're selecting a full row or column
  let selectionType: 'row' | 'column' | 'cells' = 'cells';
  if (editor.state.selection instanceof CellSelection) {
    const { left, right, top, bottom } = selectedRect(editor.state);
    const rowCount = bottom - top;
    const colCount = right - left;

    if (rowCount === 1 && colCount > 1) {
      selectionType = 'row';
    } else if (colCount === 1 && rowCount > 1) {
      selectionType = 'column';
    }
  }

  return (
    <div
      ref={toolbarRef}
      className='floating-cell-toolbar'
      style={{
        position: 'absolute',
        left: selectionRect ? selectionRect.right + window.scrollX + 1 : 0,
        top: selectionRect ? selectionRect.bottom + window.scrollY : 0,
        transform: 'translate(-100%, -150%)',
        zIndex: 500,
        pointerEvents: 'auto',
      }}
    >
      {selectionType === 'row' && (
        <>
          <button
            className='floating-cell-toolbar-button'
            onClick={handleAddRow}
          >
            <FaPlus />
          </button>
          <button
            className='floating-cell-toolbar-button'
            onClick={handleDeleteRow}
          >
            <FaMinus />
          </button>
        </>
      )}
      {selectionType === 'column' && (
        <>
          <button
            className='floating-cell-toolbar-button'
            onClick={handleAddColumn}
          >
            <FaPlus />
          </button>
          <button
            className='floating-cell-toolbar-button'
            onClick={handleDeleteColumn}
          >
            <FaMinus />
          </button>
        </>
      )}
      <button
        className='floating-cell-toolbar-button'
        onClick={() => setShowCellMenu(!showCellMenu)}
      >
        <FaEllipsisV />
      </button>
      {showCellMenu && (
        <ColorPicker
          recentColor={recentCellColor}
          onColorSelect={handlePickCellColor}
          onClose={() => setShowCellMenu(false)}
          style={{
            position: 'absolute',
            right: selectionRect && selectionRect.right > window.innerWidth - 200 ? 0 : 'auto',
            left: selectionRect && selectionRect.right <= window.innerWidth - 200 ? 0 : 'auto',
            top: selectionRect && selectionRect.bottom > window.innerHeight - 200 ? 'auto' : '100%',
            bottom:
              selectionRect && selectionRect.bottom > window.innerHeight - 200 ? '100%' : 'auto',
            zIndex: 600,
          }}
        />
      )}
    </div>
  );
};

export default EditorTableToolbar;
